import { PropTypes } from 'prop-types';
import { Controller, useForm } from 'react-hook-form';

import { useClickstreamCallback } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Button from '@zenbusiness/zui/Button';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import VisuallyHidden from '@zenbusiness/zui/VisuallyHidden';

import Fieldset from '#client/components/Fieldset';
import Form from '#client/components/Form';
import useFlowState from '#client/hooks/flow/useFlowState';
import { CHECKOUT } from '#client/pages/Checkout/constants';
import { formErrorsToTrackingErrorsMapper } from '#client/utils/formErrorsToTrackingErrorsMapper';

import PasswordField from './PasswordField';
import { isValidPassword, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from './validations';

const formClickstreamProps = {
  elementName: 'account_form'
};

function AccountForm({ defaultValues = { password: '' }, onSubmit } = {}) {
  const { contactInfo } = useFlowState();
  const { email = '' } = contactInfo;
  const captureException = useCaptureException();
  const trackFormSubmitted = useClickstreamCallback('formSubmitted');
  const trackFormSubmissionFailed = useClickstreamCallback('formSubmissionFailed');
  const { formatMessage } = useIntl();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm({
    defaultValues
  });

  const submitErrorHandler = (_errors) => {
    trackFormSubmissionFailed({ formErrors: formErrorsToTrackingErrorsMapper(_errors) });
  };

  const submitHandler = async (data) => {
    onSubmit(data);
  };

  const handleFormSubmit = (e) => {
    trackFormSubmitted(formClickstreamProps);
    handleSubmit(submitHandler, submitErrorHandler)(e).catch(captureException);
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Form
      aria-label={formatMessage({ id: 'shopping.page.checkout.account.form.ariaLabel' })}
      spacing={2}
      onSubmit={handleFormSubmit}
    >
      <VisuallyHidden as="legend">
        <FormattedMessage id="shopping.page.checkout.account.form.ariaLabel" />
      </VisuallyHidden>

      <Fieldset disabled={isSubmitting} gap={1}>
        <Text size="md" weight="medium">
          <FormattedMessage id="shopping.page.checkout.account.username" />
        </Text>
        <Text size="sm">
          <Text component="span" weight="medium">
            {email}
          </Text>{' '}
          <FormattedMessage id="shopping.page.checkout.account.email" />
        </Text>
        <Text size="md" weight="medium">
          <FormattedMessage id="shopping.page.checkout.account.setPassword" />
        </Text>
        <Controller
          control={control}
          name="password"
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <PasswordField {...field} error={error} inputRef={ref} />
          )}
          rules={{
            required: formatMessage({
              id: 'shopping.page.checkout.account.form.field.password.error.required'
            }),
            validate: (value) => {
              // isValidPassword returns true if it is valid or the error message id if invalid
              if (isValidPassword(value) === true) {
                return true; // password is valid
              }

              // Display the error message
              return formatMessage(
                {
                  id: isValidPassword(value)
                },
                {
                  minLength: PASSWORD_MIN_LENGTH,
                  maxLength: PASSWORD_MAX_LENGTH
                }
              );
            }
          }}
        />
      </Fieldset>

      <Stack spacing={1}>
        <Button
          clickstreamProps={{
            elementName: 'checkout_card_continue',
            elementValue: CHECKOUT.STEP.ACCOUNT
          }}
          color="primary"
          data-testid={`checkout-card-continue-${CHECKOUT.STEP.ACCOUNT}`}
          disabled={hasErrors}
          fullWidth
          loading={isSubmitting}
          type="submit"
          variant="contained"
        >
          <FormattedMessage id="shopping.page.checkout.saveAndContinue" />
        </Button>
      </Stack>
    </Form>
  );
}

AccountForm.propTypes = {
  defaultValues: PropTypes.shape({
    password: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired
};

export default AccountForm;
